import type { Router } from 'vue-router'

import { useAccessControl } from '@/composables/use-access-control'
import { isPostPurchaseSurveyTeamAllowed } from '@/constants/post-purchase-survey-access'
import { useUserStore } from '@/stores/user'
import { readMcpOAuthReturn } from '@/utils/mcp-oauth-return'

// 不需要登录的路由
const publicRoutes = [
  '/auth/sign-in',
  '/auth/sign-up',
  '/auth/shopify-callback',
  '/login',
  '/login-invite',
  '/register',
  '/404',
  '/403',
  '/500',
  '/custom-store',
  '/integrations/woocommerce/step1',
  '/integrations/woocommerce/step2',
  '/onboarding-retention',
  '/onboarding-retention/',
  '/public/benchmarks',
]

// 不需要应用初始化的路由（登录相关页面和自定义购物车设置页面）
const noInitRoutes = [
  '/auth/sign-in',
  '/auth/sign-up',
  '/auth/shopify-callback',
  '/login',
  '/login-invite',
  '/register',
  '/select-store',
  '/custom-store',
  '/q=source',
  '/integrations/woocommerce/step1',
  '/integrations/woocommerce/step2',
  '/auth/woo',
  '/auth/woo-checking',
  '/onboarding-retention',
  '/onboarding-retention/',
  '/public/benchmarks',
]

// 权限检查豁免路由 - 这些路由即使已登录也不需要权限检查
// 主要用于 OAuth 流程、设置向导等特殊页面
const accessControlExemptRoutes = [
  '/integrations/woocommerce/', // WooCommerce 设置向导
  '/integrations/oauth/', // OAuth 回调页面
  '/integrations/shopify', // Shopify 集成页面（可能有 OAuth）
  '/integrations/shopline', // Shopline 集成页面（可能有 OAuth）
  '/auth/', // 所有认证相关页面
  '/custom-store/', // 自定义店铺设置
]

function isAnyOnboardingPath(path: string) {
  return (
    path === '/onboarding'
    || path.startsWith('/onboarding/')
    || path === '/onboarding-retention'
    || path.startsWith('/onboarding-retention/')
  )
}

function isRetentionOnboardingPath(path: string) {
  return path === '/onboarding-retention' || path.startsWith('/onboarding-retention/')
}

async function resolveRetentionRedirectPath(): Promise<string> {
  try {
    const { onboardingProgressGetApi } = await import('@/services/api/platform.api')
    const { useOnboardingRetentionProgressStore } = await import('@/stores/onboarding-retention-progress')
    const retentionData = await onboardingProgressGetApi({ version: 'retention-v1' })
    const retentionStore = useOnboardingRetentionProgressStore()
    if (retentionData) {
      retentionStore.progress = retentionData
      if (!retentionStore.selectedEsp) {
        const espFromData = (retentionData.progress_data || {}).selectedEsp as any
        if (espFromData === 'klaviyo' || espFromData === 'omnisend' || espFromData === 'cartsee' || espFromData === 'manba' || espFromData === 'activecampaign')
          retentionStore.selectedEsp = espFromData
      }
    }
    if (retentionData?.is_completed)
      return '/home'
    return retentionStore.getResumePath() || '/onboarding-retention/step0'
  }
  catch (e) {
    console.warn('[retention-guard] resolveRetentionRedirectPath failed, fallback to step0:', e)
    return '/onboarding-retention/step0'
  }
}

export function authGuard(router: Router) {
  router.beforeEach(async (to, from) => {
    const userStore = useUserStore()
    // 【注意】：toPath 必须最先声明，后续的 benchmark 拦截、登录检查等流程都会用到它；
    // 如果在局部 const 声明之后才使用 toPath 会触发 TDZ（Cannot access 'toPath' before initialization）
    const toPath = to.path

    // The consent page performs authenticated server requests itself. It must
    // remain reachable without the selected-store/onboarding application flow.
    if (toPath === '/oauth/consent') {
      userStore.initTokenAndSession()
      return true
    }
    // Resume a short-lived OAuth transaction after existing login succeeds,
    // before the normal select-store page can switch the browser's active team.
    if (toPath === '/select-store' || toPath === '/home') {
      const pendingPath = readMcpOAuthReturn(
        sessionStorage.getItem('mcpOAuthReturn'),
        Boolean(sessionStorage.getItem('token')),
      )
      if (pendingPath) {
        sessionStorage.removeItem('mcpOAuthReturn')
        return pendingPath
      }
    }

    // 【严格匹配规则】：只有以下 1 种情况算 benchmark 来源
    //   必须是 utm_source=xxx，且 xxx 字符串里包含 "benchmark"（不区分大小写）
    //   示例：?utm_source=nccpa_benchmark / ?utm_source=BENCHMARK_google_v2
    //   ✗ 不匹配：?benchmark=xxx / ?utm_campaign=xxx_benchmark
    const _checkBenchmarkHit = (obj: Record<string, any> | URLSearchParams): { hit: boolean, value: string | null } => {
      try {
        const _get = (k: string): string | null => {
          if (obj instanceof URLSearchParams) {
            const v = obj.get(k)
            return v !== null ? String(v) : null
          }
          const v = (obj as any)[k]
          return v !== undefined && v !== null ? String(v) : null
        }
        const utmSource = _get('utm_source')
        if (utmSource !== null && utmSource !== '' && utmSource.toLowerCase().includes('benchmark')) {
          return { hit: true, value: utmSource }
        }
      }
      catch (_e) {}
      return { hit: false, value: null }
    }

    const _captureBenchmarkFlag = (): string | null => {
      try {
        const flagKey = 'benchmark_onboarding_flag'
        const pendingKey = 'benchmark_onboarding_pending'
        if (typeof window === 'undefined')
          return null
        let captured: string | null = null
        let matched: string = '(none)'
        // 1) hash 内部 query（vue-router 标准，如 #/home?benchmark=1 / #/home?utm_source=nccpa_benchmark）
        {
          const r = _checkBenchmarkHit(to.query || {})
          if (r.hit && r.value) {
            captured = r.value
            matched = 'to.query'
          }
        }
        // 2) hash 前的 location.search（如 /?benchmark=1#/home / /?utm_source=nccpa_benchmark#/login）
        if (!captured && window.location.search) {
          const sp = new URLSearchParams(window.location.search)
          const r = _checkBenchmarkHit(sp)
          if (r.hit && r.value) {
            captured = r.value
            matched = 'location.search'
          }
        }
        // 3) hash 本身字符串里带 query（兜底）
        if (!captured && window.location.hash) {
          const h = window.location.hash
          const qIdx = h.indexOf('?')
          if (qIdx !== -1) {
            const sp = new URLSearchParams(h.slice(qIdx + 1))
            const r = _checkBenchmarkHit(sp)
            if (r.hit && r.value) {
              captured = r.value
              matched = 'location.hash-inner'
            }
          }
        }
        if (captured) {
          localStorage.setItem(flagKey, captured)
          localStorage.setItem(pendingKey, '1')
          console.warn('[benchmark] 捕获成功:', { matched, captured, href: window.location.href })
          return captured
        }
        return captured
      }
      catch (_e) { return null }
    }
    const currentBenchmark = _captureBenchmarkFlag()
    // 【核心区分标记】：
    //   thisNavHasBenchmark === true  → 「这次导航 URL 本身」真的命中了 utm_source=*benchmark（才允许「第一时间防御」和「最后保护拦截」无条件跳 Goal-First）
    //   thisNavHasBenchmark === false → 「这次 URL 根本没带 benchmark 参数」，哪怕 localStorage 里有历史遗留的 flag/pending，
    //                                    也绝不主动跳 Goal-First（除非后续 initApp 后真正判断 needOnboarding=true 且店铺确实未完成 onboarding，
    //                                    才允许用历史 pending 走 Goal-First——这种是 login/OAuth 跳转链路中途丢参的合法场景）
    const thisNavHasBenchmark = currentBenchmark !== null && currentBenchmark !== ''

    // 【立即清理历史污染】：如果「这次 URL 根本没带 benchmark」，且用户当前访问的就是 retention onboarding 路径，
    // 那就说明用户的意图就是走 retention；直接把 localStorage 遗留的 benchmark 标记清掉，
    // 避免后续任何分支再把用户拉回 Goal-First。
    if (!thisNavHasBenchmark && isRetentionOnboardingPath(toPath)) {
      console.warn('[benchmark] 本次 URL 无 benchmark 且访问 retention → 清掉历史遗留 flag/pending，保持 retention 不动')
      try {
        localStorage.removeItem('benchmark_onboarding_pending')
        localStorage.removeItem('benchmark_onboarding_flag')
      }
      catch (_e) {}
    }

    // 判断「是否需要强制跳 Goal-First onboarding」：
    // 【严格双锁判断，绝对杜绝无参数误跳 Goal-First】
    // 合法 benchmark 只有两种情况，任一满足即认为是真 benchmark：
    //   1) 本次导航 URL 真的命中了 utm_source=*benchmark（thisNavHasBenchmark === true），
    //      即使 localStorage 暂时没写入（比如 onMounted 还没跑完），也要认定为真 benchmark。
    //   2) localStorage 里双锁同时存在：
    //        benchmark_onboarding_pending === '1'
    //        AND benchmark_onboarding_flag 非空（非 null / 非空字符串）
    //      （双锁都在 = 用户上一轮真的命中 benchmark，并且还没走完 Goal-First onboarding，
    //       这时候刷新页面 / 后续路由跳转仍要继续走 Goal-First）
    // ❌ 单锁（只有 pending=1 且 flag 为空、或只有 flag 没 pending）一律 false！
    //    因为那一定是历史遗留脏数据，绝对不能把不带参数的普通用户拉回 Goal-First。
    const _isBenchmarkPending = (): boolean => {
      try {
        if (thisNavHasBenchmark)
          return true
        const p = localStorage.getItem('benchmark_onboarding_pending')
        const f = localStorage.getItem('benchmark_onboarding_flag')
        return p === '1' && f !== null && f !== ''
      }
      catch (_e) { return false }
    }
    // 清除 benchmark 全套标记（只在真正完成分流/进入 step1 时调用）
    const _clearBenchmarkPendingAll = () => {
      try {
        localStorage.removeItem('benchmark_onboarding_pending')
        localStorage.removeItem('benchmark_onboarding_flag')
      }
      catch (_e) {}
    }

    // 【第一时间防御】：只有「这次导航 URL 真的带了 utm_source=*benchmark」，
    // 且当前正在访问 retention onboarding 路径，且 benchmark pending=1，才立刻拦截跳 Goal-First。
    // （解决：新一轮 benchmark 访问下，浏览器仍展示上一次遗留的 retention step0 页面的问题）
    // 【关键】：如果这次 URL 根本没带 benchmark，即使 localStorage 有历史 pending，也不拦截——
    //          否则会把不带参数的普通用户也强行拉回 Goal-First。
    if (thisNavHasBenchmark && _isBenchmarkPending() && isRetentionOnboardingPath(toPath)) {
      console.warn('[benchmark] 本次 URL 命中 benchmark + pending + 当前访问 retention → 拦截跳 Goal-First /onboarding')
      return { path: '/onboarding' }
    }

    const retentionLockKey = 'retention_onboarding_lock'

    // 0. WooCommerce OAuth: 最优先检查 - 如果有 pending shop 且用户已登录，立即重定向
    // 但如果目标已经是 /auth/woo，就不要再重定向（避免无限循环）
    const pendingWooShop = localStorage.getItem('pendingWooShop')
    const pendingWooPath = localStorage.getItem('pendingWooPath') // WooCommerce OAuth 专用路径（/auth/woo 或 /auth/woo-checking）
    if (pendingWooShop && sessionStorage.getItem('token') && !toPath.startsWith('/auth/woo')) {
      console.warn('WooCommerce OAuth: Found pending shop with logged in user, redirecting to:', pendingWooPath || '/auth/woo', pendingWooShop)
      // 优先使用 pendingWooPath（WooCommerce 专用），如果没有则使用 redirectAfterLogin，最后默认 /auth/woo
      const targetPath = pendingWooPath || localStorage.getItem('redirectAfterLogin') || '/auth/woo'
      return { path: targetPath, query: { shop: pendingWooShop } }
    }

    // 1. 检查是否是公共路由（不需要登录）
    // 注意：对于 meta.auth === false 的路由，需要额外检查是否在权限检查豁免列表中
    // 如果不在豁免列表中，已登录用户仍需要进行权限检查
    const isInPublicRouteList = publicRoutes.some(route => toPath.startsWith(route))
    const isMetaAuthFalse = to.meta.auth === false
    const isPublicRoute = (isInPublicRouteList || isMetaAuthFalse) && toPath !== '/login' && toPath !== '/login-invite'

    // 对于公共路由，检查用户是否已登录
    // 如果未登录，直接允许访问
    // 如果已登录，需要继续后续的权限检查流程
    if (isPublicRoute) {
      const sessionStatus = userStore.initTokenAndSession()
      if (sessionStatus.needLogin) {
        // 未登录用户访问公共路由，直接允许
        return true
      }
      // 已登录用户访问公共路由，继续后续检查（包括权限检查）
      // 但如果是在权限豁免列表中，可以直接允许
      const isAccessControlExempt = accessControlExemptRoutes.some(route => toPath.startsWith(route))
      if (isAccessControlExempt) {
        return true
      }
      // 继续后续检查...
    }

    // 2. 检查登录状态和团队选择状态
    const sessionStatus = userStore.initTokenAndSession()

    // 如果需要登录
    if (sessionStatus.needLogin) {
      // 如果已经在登录页或邀请页，避免重定向
      if (toPath === '/login' || toPath === '/login-invite') {
        return true
      }

      // retention-onboarding：允许未登录用户直接访问（用于本地 UI 调试 / 未登录直接打开 step0）
      if (isRetentionOnboardingPath(toPath)) {
        return true
      }

      // WooCommerce OAuth: 保存pending shop信息，以便登录后继续OAuth流程
      if (toPath.startsWith('/auth/woo')) {
        const shopParam = to.query.shop as string
        if (shopParam) {
          localStorage.setItem('pendingWooShop', shopParam)
          localStorage.setItem('pendingWooPath', toPath) // 保存完整路径（/auth/woo 或 /auth/woo-checking）
          console.warn('WooCommerce OAuth: Saved pending shop and path for after login:', shopParam, toPath)
        }
      }

      // 记录用户尝试访问的路径，以便登录后跳转
      if (!['/', '/login', '/login-invite'].includes(toPath)) {
        localStorage.setItem('redirectAfterLogin', toPath)
      }
      // 跳转登录时，如果当前 URL 带 benchmark 参数，拼到 login 的 query 里一路传下去
      // 确保登录页 onMounted 也能再次捕获（双保险：sessionStorage 已写入 + login URL 显性携带）
      const _benchmarkForLogin = (() => {
        if (currentBenchmark !== null && currentBenchmark !== '')
          return currentBenchmark
        try {
          return localStorage.getItem('benchmark_onboarding_flag')
        }
        catch (_e) { return null }
      })()
      if (_benchmarkForLogin !== null && _benchmarkForLogin !== '') {
        return { path: '/login', query: { benchmark: _benchmarkForLogin } }
      }
      return { path: '/login' }
    }

    // 如果是去创建自定义店铺页面或 WooCommerce 创建店铺页面（step1），允许访问
    if (toPath.startsWith('/custom-store') || toPath.startsWith('/integrations/woocommerce/step1')) {
      return true
    }

    // 已登录但需要选择店铺时，retention-onboarding 也直接放行（避免本地调试被 select-store 拦截）
    if (sessionStatus.needSelectStore && isRetentionOnboardingPath(toPath)) {
      return true
    }

    // 如果已登录但需要选择店铺
    if (sessionStatus.needSelectStore) {
      // WooCommerce OAuth 特殊处理：如果目标是 /auth/woo* 且有 shop 参数，直接允许通过
      // OAuth 流程不需要先选择店铺，因为 shop 参数已经指定了要连接的店铺
      if (toPath.startsWith('/auth/woo') && (to.query.shop || localStorage.getItem('pendingWooShop'))) {
        console.warn('WooCommerce OAuth: Allowing access to /auth/woo* despite needSelectStore')
        return true
      }

      // 如果已经在选择店铺页，避免重定向
      if (toPath === '/select-store') {
        return true
      }
      // 记录用户尝试访问的路径，以便选择店铺后跳转
      if (!['/', '/select-store'].includes(toPath)) {
        localStorage.setItem('redirectAfterSelectStore', toPath)
      }
      return { path: '/select-store' }
    }

    // 如果用户已经登录且有完整的团队信息，但访问登录页面，则重定向到主页
    if (toPath === '/login' && !sessionStatus.needLogin && !sessionStatus.needSelectStore) {
      return { path: '/home' }
    }

    // 2.5 检查 self 类型店铺是否完成 webpixel 设置
    // 如果是 self 类型且 webpixel_time 为空，需要跳转到 custom-store/step2 完成设置
    if (!sessionStatus.needLogin && !sessionStatus.needSelectStore) {
      const currentTeam = userStore.user?.team
      const isCustomStoreRoute = toPath.startsWith('/custom-store')
      const isWooCommerceRoute = toPath.startsWith('/integrations/woocommerce')
      const isWooOAuthRoute = toPath.startsWith('/auth/woo') // WooCommerce OAuth 流程不应该被 webpixel 检查拦截

      // 检查 self 类型店铺
      if (currentTeam?.shop_type === 'self' && !currentTeam.webpixel_time && !isCustomStoreRoute && !isWooOAuthRoute) {
        // 如果不在 custom-store 路由中，且需要完成设置，则跳转到 step2
        return { path: '/custom-store/step2' }
      }

      // 检查 woo 类型店铺
      if (currentTeam?.shop_type === 'woocommerce' && !currentTeam.webpixel_time && !isWooCommerceRoute && !isWooOAuthRoute) {
        // 如果不在 woocommerce 路由中，且需要完成插件安装，则跳转到 woocommerce 插件安装页面
        // 跳转到 WooCommerce 插件安装步骤（step 2）
        return { path: '/integrations/woocommerce/step2' }
      }
    }

    // 3. 如果已经有完整的登录信息，检查是否需要应用初始化
    const needsInit = !noInitRoutes.some(route => toPath.startsWith(route))

    // retention-onboarding 页面：即使未登录 / 未选择店铺 / 未初始化也直接放行
    // 页面内部自己的 ensureLoaded 会再按需从后端取进度，失败时不报错并展示默认状态
    if (isRetentionOnboardingPath(toPath)) {
      // 从其他页面直接跳回 retention onboarding 入口时，自动落到对应 step（缺 ESP → step0，有 ESP → stepN）
      // 只有当目标就是 `/onboarding-retention` 入口时才重定向；精确 step 路径（/step0~/step4）直接渲染
      if (toPath === '/onboarding-retention') {
        try {
          const { useOnboardingRetentionProgressStore } = await import('@/stores/onboarding-retention-progress')
          const retentionStore = useOnboardingRetentionProgressStore()
          const resume = retentionStore.getResumePath()
          if (resume && resume !== toPath)
            return { path: resume }
        }
        catch (e) {
          console.warn('[retention-guard] getResumePath for entry redirect failed:', e)
          return { path: '/onboarding-retention/step0' }
        }
      }
      return true
    }

    // 无论是否已初始化，如果需要初始化的路由都要经过这个检查
    if (needsInit) {
      // 如果还没有初始化，先执行初始化
      if (!userStore.isAppInitialized) {
        try {
          // eslint-disable-next-line no-console
          console.info('开始应用初始化...')

          // 执行应用初始化
          const initResult = await userStore.initApp()

          // 在开发环境中，初始化完成后强制切换为 Pro 计划，避免后续订阅检查拦截
          // if (import.meta.env.DEV) {
          //   userStore.setPlanPro()
          // }

          // eslint-disable-next-line no-console
          console.info('应用初始化完成', JSON.stringify(userStore.user.sub_items_price))

          // 【终极防御 1 / 首次初始化】：如果这次 URL 根本没带 benchmark 参数，
          // 先清掉 localStorage 里遗留的 benchmark flag/pending 脏标记，
          // 绝对保证后面的分流不会把"真·普通用户"误判成 benchmark 用户 → 跳 Goal-First。
          // （如果这次 URL 真带 benchmark，_captureBenchmarkFlag 在更前面的位置已经把双锁写回了）
          if (!thisNavHasBenchmark) {
            const before1 = localStorage.getItem('benchmark_onboarding_pending')
            const before2 = localStorage.getItem('benchmark_onboarding_flag')
            _clearBenchmarkPendingAll()
            if (before1 || before2) {
              console.warn('[benchmark] 本次 URL 无 benchmark → 首次初始化前清除历史脏标记:', {
                pendingBefore: before1,
                flagBefore: before2,
              })
            }
          }

          // 1. 首先检查是否需要跳转到订阅页面（参考老系统逻辑）
          // 订阅检查优先于 onboarding 检查
          if (userStore.user.sub_items_price?.access_flag === false || !userStore.user.sub_items_price?.plan?.item_plan) {
            const qSourceDone = localStorage.getItem('q_source_done')
            const teamCreatedAt = userStore.user?.team?.created_at
            const teamCreatedAtTime = teamCreatedAt ? Date.parse(teamCreatedAt) : Number.NaN
            const qSourceLaunchAt = new Date('2026-05-10T00:00:00Z')
            const shouldAskQSource = qSourceDone !== '1'
              && Number.isFinite(teamCreatedAtTime)
              && teamCreatedAtTime >= qSourceLaunchAt.getTime()

            if (shouldAskQSource) {
              return { path: '/q=source' }
            }

            // benchmark 来源用户：即使跳过 q=source，也强制走 Goal-First onboarding（/onboarding）
            // 不走 retention onboarding
            // 【关键】：必须同时满足「本次 URL 命中 benchmark OR 历史 pending 合法」才允许跳 Goal-First，
            //          否则任何单一 localStorage 残留都绝对不允许跳 Goal-First。
            const _benchmarkFlag = localStorage.getItem('benchmark_onboarding_flag')
            const _benchmarkPending = localStorage.getItem('benchmark_onboarding_pending')
            const hasBenchmarkFlag = thisNavHasBenchmark
              || (_benchmarkPending === '1' && _benchmarkFlag !== null && _benchmarkFlag !== '')

            if (hasBenchmarkFlag) {
              console.warn('[benchmark] 订阅未激活 + shouldAskQSource=false：benchmark 合法，跳 Goal-First /onboarding')
              localStorage.setItem(retentionLockKey, '1')
              // 如果已经在 Goal-First onboarding 路径里，直接放行
              if (toPath === '/onboarding' || toPath.startsWith('/onboarding/')) {
                return true
              }
              return { path: '/onboarding' }
            }

            if (isRetentionOnboardingPath(toPath)) {
              localStorage.setItem(retentionLockKey, '1')
              return true
            }

            localStorage.setItem(retentionLockKey, '1')
            const retentionTarget = await resolveRetentionRedirectPath()
            return { path: retentionTarget }
          }

          // 2. 订阅完成后，检查是否需要 onboarding，并根据 benchmark 标记选 Goal-First 还是 retention
          // 【关键】：benchmark pending 的优先级 != 无条件强制
          //   - 如果 needOnboarding=true（确实还没完成 onboarding）→ benchmark 生效，跳 Goal-First /onboarding
          //   - 如果 needOnboarding=false（后端判断已完成 setup 的老店铺）→ benchmark 标记无效，清掉，正常放行
          //   - Goal-First onboarding 首页（/onboarding）允许直接 URL 访问，即使已完成也放行（用于特殊入口）
          const pendingFirstInit = _isBenchmarkPending()
          // 先判断：访问 Goal-First onboarding 本身的路径，一律放行（无论是否 pending，是用户主动访问/或上一步跳转的目标）
          if (toPath === '/onboarding' || toPath.startsWith('/onboarding/')) {
            return true
          }
          // 访问 retention onboarding 路径：如果 benchmark pending 且 needOnboarding=true，拦截跳 Goal-First
          if (isRetentionOnboardingPath(toPath)) {
            if (pendingFirstInit) {
              // 这里先让下面的 needOnboarding 判断决定
            }
          }

          // 3. 检查是否需要跳转到 onboarding（统一分流：benchmark + needOnboarding 一起判断）
          // 使用 try-catch 捕获接口错误，区分"真正需要 onboarding"和"接口请求失败"
          try {
            if (initResult.needOnboarding) {
              // benchmark 来源用户（needOnboarding=true 前提下）：
              //   强制走 Goal-First onboarding（/onboarding），忽略后端返回的 redirectTo
              // 无 benchmark 的普通用户：**无论如何都走 retention onboarding**，
              //   后端返回的 onboardingRedirectTo 如果合法（retention 路径）就用，否则 fallback 到 retention 标准入口
              const hasBenchmark = _isBenchmarkPending()
              let redirectTo: string
              if (hasBenchmark) {
                redirectTo = '/onboarding'
              }
              else {
                if (initResult.onboardingRedirectTo && isRetentionOnboardingPath(initResult.onboardingRedirectTo)) {
                  redirectTo = initResult.onboardingRedirectTo
                }
                else {
                  try {
                    redirectTo = await resolveRetentionRedirectPath()
                  }
                  catch (e) {
                    console.warn('[retention-guard] resolveRetentionRedirectPath failed during first init:', e)
                    redirectTo = '/onboarding-retention/step0'
                  }
                }
              }
              console.warn('[benchmark] needOnboarding=true:', { hasBenchmark, redirectTo, backendRedirect: initResult.onboardingRedirectTo })
              // 检查目标页面是否与当前页面相同，避免无限循环
              if (toPath !== redirectTo && !isAnyOnboardingPath(toPath)) {
                console.warn('用户需要完成 onboarding，跳转到:', redirectTo)
                // 记录用户尝试访问的路径，以便完成后跳转回来
                if (toPath !== '/' && toPath !== '/home') {
                  localStorage.setItem('redirectAfterOnboarding', toPath)
                }
                return { path: redirectTo }
              }
              // 正在访问 retention onboarding 路径但 benchmark 需要 Goal-First → 拦截
              if (hasBenchmark && isRetentionOnboardingPath(toPath)) {
                console.warn('[benchmark] needOnboarding=true 但访问 retention，拦截跳 Goal-First /onboarding')
                return { path: '/onboarding' }
              }
            }
            else {
              // needOnboarding=false：后端判断已完成 setup 的老店铺
              if (pendingFirstInit) {
                console.warn('[benchmark] 后端 needOnboarding=false（老店铺已完成 setup）：清除 benchmark 标记，正常放行不再拉回 onboarding')
                _clearBenchmarkPendingAll()
              }

              // 如果已经有任务列表（已完成 onboarding），但当前访问的是 retention onboarding 首页，重定向到首页
              // 注：Goal-First 版 /onboarding 允许直接 URL 访问，即使已完成也可再次进入（用于 benchmark 等特殊入口）
              if (toPath === '/onboarding-retention') {
                console.warn('用户已完成 onboarding，重定向到首页')
                return { path: '/home' }
              }
            }
          }
          catch (onboardingError) {
            // 接口请求失败，记录错误但允许用户继续访问
            console.error('检查 onboarding 状态失败，允许用户继续访问:', onboardingError)
            // 不跳转到 onboarding，让用户继续访问目标页面
            // 这样避免了因为接口错误而误导用户
          }
        }
        catch (error) {
          console.error('应用初始化失败:', error)
          // 初始化失败，清除用户状态并跳转到登录页
          // userStore.logout()
          return { path: '/login' }
        }
      }
      else {
        if (userStore.user.sub_items_price?.access_flag === false || !userStore.user.sub_items_price?.plan?.item_plan) {
          // 【终极防御 2 / 已初始化 → 订阅未激活】：这次 URL 无 benchmark → 先清历史遗留脏锁，
          // 防止后面的 _hasStrictBenchmark 误判把普通用户拉回 Goal-First
          if (!thisNavHasBenchmark) {
            const before1 = localStorage.getItem('benchmark_onboarding_pending')
            const before2 = localStorage.getItem('benchmark_onboarding_flag')
            _clearBenchmarkPendingAll()
            if (before1 || before2) {
              console.warn('[benchmark] 本次 URL 无 benchmark → 订阅未激活分支清除历史脏标记:', {
                pendingBefore: before1,
                flagBefore: before2,
              })
            }
          }
          const qSourceDone = localStorage.getItem('q_source_done')
          const teamCreatedAt = userStore.user?.team?.created_at
          const teamCreatedAtTime = teamCreatedAt ? Date.parse(teamCreatedAt) : Number.NaN
          const qSourceLaunchAt = new Date('2026-05-10T00:00:00Z')
          const shouldAskQSource = qSourceDone !== '1'
            && Number.isFinite(teamCreatedAtTime)
            && teamCreatedAtTime >= qSourceLaunchAt.getTime()

          if (shouldAskQSource) {
            console.warn('[benchmark] shouldAskQSource=true，先跳问卷 /q=source（benchmark 分流在问卷完成后处理）')
            return { path: '/q=source' }
          }

          // benchmark 来源用户：即使跳过 q=source（老店铺 / 问卷已完成），也强制走 Goal-First onboarding（/onboarding）
          // 订阅未激活的情况下，默认就是要进 onboarding 的，这里只改变方向：Goal-First vs retention
          // 【关键】：必须「本次 URL 真命中 benchmark OR（pending=1 且 flag 都在）」才允许跳 Goal-First，
          //          单一 localStorage 残留绝不跳 Goal-First；无 benchmark 就一定走 retention。
          const _hasStrictBenchmark = thisNavHasBenchmark || _isBenchmarkPending()
          if (_hasStrictBenchmark) {
            console.warn('[benchmark] 订阅未激活 + shouldAskQSource=false：benchmark 合法，跳 Goal-First /onboarding')
            localStorage.setItem(retentionLockKey, '1')
            if (toPath === '/onboarding' || toPath.startsWith('/onboarding/')) {
              return true
            }
            return { path: '/onboarding' }
          }

          if (isRetentionOnboardingPath(toPath)) {
            localStorage.setItem(retentionLockKey, '1')
            return true
          }

          localStorage.setItem(retentionLockKey, '1')
          const retentionTarget = await resolveRetentionRedirectPath()
          return { path: retentionTarget }
        }
        // 【终极防御 3 / 已初始化 → 订阅已激活】：这次 URL 无 benchmark → 先清历史遗留脏锁，
        // 防止 pendingAlreadyInit / 后续 initOnboarding 检查误判把普通用户拉回 Goal-First
        if (!thisNavHasBenchmark) {
          const before1 = localStorage.getItem('benchmark_onboarding_pending')
          const before2 = localStorage.getItem('benchmark_onboarding_flag')
          _clearBenchmarkPendingAll()
          if (before1 || before2) {
            console.warn('[benchmark] 本次 URL 无 benchmark → 订阅已激活分支清除历史脏标记:', {
              pendingBefore: before1,
              flagBefore: before2,
            })
          }
        }
        // 订阅已激活 + 系统已初始化完成：benchmark 分流必须「严格满足 thisNavHasBenchmark 或双锁都在」+ needOnboarding=true 才跳 Goal-First
        //   - needOnboarding=true（还没完成 onboarding 的店铺）+ benchmark 合法 → Goal-First
        //   - 其他一切情况（无 benchmark / 只有单一 localStorage 残留 / needOnboarding=false）→ 正常走 retention 或 home，绝不主动跳 Goal-First
        const pendingAlreadyInit = thisNavHasBenchmark || _isBenchmarkPending()
        // ① 用户直接访问 Goal-First onboarding 本身的路径：一律放行（特殊入口 / 上一步跳转目标，产品要求允许）
        if (toPath === '/onboarding' || toPath.startsWith('/onboarding/')) {
          return true
        }
        // 无 benchmark 的普通用户：绝对不进入任何「拉回 Goal-First」的判断分支，直接放行让后面走 retention
        if (!pendingAlreadyInit) {
          // 无 benchmark + 合法 retention 路径：放行
          if (isRetentionOnboardingPath(toPath)) {
            return true
          }
          // 无 benchmark + 其他路径：直接跳到后面的 onboarding-retention 二次检查，让它走原 retention 逻辑
        }
        else {
          // ② 有合法 benchmark 标记的用户：访问 retention onboarding 路径 → 用下面的 initOnboarding 统一判断
          if (isRetentionOnboardingPath(toPath)) {
            // 让下面的 initOnboarding 检查统一判断
          }
          // ③ 有合法 benchmark + 其他路径（home 等）：先查 needOnboarding，再决定要不要拉回 Goal-First
          try {
            const onboardingResult = await userStore.initOnboarding()
            console.warn('[benchmark] already-init + subscription ok + strict benchmark，initOnboarding check:', {
              needOnboarding: onboardingResult.needOnboarding,
              redirectTo: onboardingResult.redirectTo,
              thisNavHasBenchmark,
            })
            if (onboardingResult.needOnboarding) {
              // 还没完成 onboarding 的店铺：benchmark 合法 → Goal-First
              console.warn('[benchmark] needOnboarding=true + benchmark 合法 → 跳 Goal-First /onboarding')
              localStorage.setItem(retentionLockKey, '1')
              return { path: '/onboarding' }
            }
            else {
              // 已完成 setup 的老店铺：benchmark 标记作废，清掉后正常放行
              console.warn('[benchmark] 后端 needOnboarding=false（老店铺已完成 setup）：清除 benchmark 标记，正常放 home')
              _clearBenchmarkPendingAll()
              // 如果用户正在访问 retention onboarding 首页，重定向到 home
              if (toPath === '/onboarding-retention') {
                console.warn('用户已完成 onboarding，重定向到首页')
                return { path: '/home' }
              }
            }
          }
          catch (error) {
            console.error('[benchmark] initOnboarding 检查失败，清除 benchmark 标记安全放行:', error)
            _clearBenchmarkPendingAll()
          }
        }

        // benchmark 来源用户：如果尝试访问 retention onboarding 路径，
        // 再次根据「本次 URL 命中 benchmark」+ pending 保护性拦截跳 Goal-First onboarding
        // 【关键】：如果这次 URL 没带 benchmark，哪怕 localStorage 有历史 pending，也不拦截——
        //          否则不带参数的普通用户会被历史标记污染，永远进不了 retention。
        if (thisNavHasBenchmark && _isBenchmarkPending() && isRetentionOnboardingPath(toPath)) {
          console.warn('[benchmark] 本次 URL 命中 benchmark + pending + 访问 retention → 跳 Goal-First /onboarding')
          return { path: '/onboarding' }
        }

        // 已经初始化过，如果访问的是 retention onboarding 问卷页面，需要再次检查状态
        // 注：Goal-First 版 /onboarding 允许直接 URL 访问，即使已完成也可再次进入（用于 benchmark 等特殊入口）
        if (toPath === '/onboarding-retention') {
          if (isAnyOnboardingPath(from.path) || to.query?.q) {
            return true
          }
          try {
            const onboardingResult = await userStore.initOnboarding()
            if (!onboardingResult.needOnboarding) {
              // 不需要 onboarding，重定向到首页
              console.warn('用户已完成 onboarding（已初始化检查），重定向到首页')
              return { path: '/home' }
            }
            // 需要 onboarding 但有特定重定向路径
            if (onboardingResult.redirectTo && onboardingResult.redirectTo !== '/onboarding' && onboardingResult.redirectTo !== '/onboarding-retention') {
              console.warn('用户需要继续 onboarding，重定向到:', onboardingResult.redirectTo)
              return { path: onboardingResult.redirectTo }
            }
            // 需要 onboarding 问卷，允许访问
          }
          catch (error) {
            console.error('检查 onboarding 状态失败，允许用户继续访问:', error)
            // 接口出错，允许用户访问 onboarding 页面
            // 避免因为接口错误而阻止用户正常使用
          }
        }
      }
    }

    const normalizedPath = toPath.endsWith('/') ? toPath.slice(0, -1) : toPath
    if (normalizedPath === '/post-purchase-survey'
      && !isPostPurchaseSurveyTeamAllowed(userStore.user?.team_id || userStore.user?.team?.id)) {
      return { path: '/home' }
    }
    const isBillingRoute = normalizedPath === '/billing' || normalizedPath === '/billing-v1' || normalizedPath === '/billing-recapture'
    if (isBillingRoute) {
      const targetBillingPath = userStore.getBillingPath()
      if (normalizedPath === '/billing-v1' && targetBillingPath === '/billing-recapture') {
        return { path: targetBillingPath }
      }
      if (normalizedPath === '/billing-v1')
        return true
      if (normalizedPath !== targetBillingPath) {
        return { path: targetBillingPath }
      }
    }

    const isSegmentsRoute = normalizedPath === '/store/segments' || normalizedPath === '/store/segments-v1'
    if (isSegmentsRoute) {
      const targetSegmentsPath = userStore.getSegmentsPath()
      if (normalizedPath !== targetSegmentsPath) {
        return { path: targetSegmentsPath }
      }
    }

    const isWebAnalysisRoute = normalizedPath === '/attribution/web-analysis' || normalizedPath === '/attribution/web-analysis-v1' || normalizedPath === '/attribution/explorer-web-analysis'
    if (isWebAnalysisRoute) {
      const targetWebAnalysisPath = userStore.getWebAnalysisPath()
      if (normalizedPath !== targetWebAnalysisPath) {
        return { path: targetWebAnalysisPath }
      }
    }

    const isDestinationsRoute = normalizedPath === '/conversion-feed/destinations' || normalizedPath === '/conversion-feed/destinations-v1'
    if (isDestinationsRoute) {
      if (normalizedPath === '/conversion-feed/destinations-v1')
        return true
      const targetDestinationsPath = userStore.getDestinationsPath()
      if (normalizedPath !== targetDestinationsPath) {
        return { path: targetDestinationsPath }
      }
    }

    // 3.5 Access Control Check (Enterprise Feature)
    // 使用 useAccessControl 进行权限检查，权限配置在 initApp 时已加载
    const { checkPageAccess, isAccessControlAvailable } = useAccessControl()

    // 只有当用户不是管理员或创建者时才进行权限检查
    if (userStore.isAppInitialized && !userStore.user.administer_flag && !userStore.user.creator_flag) {
      // 始终允许访问错误页面和基础页面
      const alwaysAllowedPaths = ['/403', '/404', '/500', '/401', '/503', '/access-denied', '/profile', '/select-store', '/onboarding', '/onboarding-retention']
      const isAlwaysAllowed = alwaysAllowedPaths.some(allowedPath => toPath.startsWith(allowedPath))

      // 检查是否是权限检查豁免路由（OAuth、设置向导等）
      const isAccessControlExempt = accessControlExemptRoutes.some(route => toPath.startsWith(route))

      if (!isAlwaysAllowed && !isAccessControlExempt && !checkPageAccess(toPath)) {
        console.warn('Access Denied (Access Control):', toPath)
        if (isAccessControlAvailable.value) {
          // 保存被拒绝访问的路径，以便权限刷新后尝试返回
          localStorage.setItem('redirectAfterAccessRefresh', toPath)
          // 跳转到专门的权限拒绝页面，支持刷新权限
          return { path: '/access-denied' }
        }
        return true
      }
    }

    // 4. 所有验证通过，允许访问
    return true
  })
}
